/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Payment Gateways Multi-Channel Configuration View (Task 6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Building2,
  ShieldCheck,
  Globe,
  Save,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Copy,
  Check,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { getAdminDomains } from '../lib/urls';
import { adminClient } from '../lib/api-client';
import type {
  SystemSettings,
  PaymentGatewaysSettings,
  UpdateSystemSettingsInput,
} from '@sofia/shared-types';

export const PaymentGatewaysConfigView: React.FC = () => {
  const { t } = useI18n();
  const domains = getAdminDomains();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Password visibility states
  const [showStripeSecret, setShowStripeSecret] = useState(false);
  const [showStripeWebhook, setShowStripeWebhook] = useState(false);
  const [showPaypalSecret, setShowPaypalSecret] = useState(false);
  const [showPaddleApiKey, setShowPaddleApiKey] = useState(false);
  const [showPaddleWebhook, setShowPaddleWebhook] = useState(false);

  // Form draft state
  const [draft, setDraft] = useState<PaymentGatewaysSettings>({
    stripe: {
      enabled: false,
      sandbox: true,
      publishableKey: '',
      secretKey: '',
      webhookSecret: '',
    },
    paypal: {
      enabled: false,
      sandbox: true,
      clientId: '',
      clientSecret: '',
      webhookId: '',
    },
    paddle: {
      enabled: false,
      sandbox: true,
      vendorId: '',
      clientSideToken: '',
      apiKey: '',
      webhookSecret: '',
    },
    bankTransfer: {
      enabled: false,
      bankName: 'Vietcombank (Ngân hàng TMCP Ngoại thương Việt Nam)',
      accountNumber: '1029384756',
      accountHolder: 'FOUR ELEMENTS CORP',
      branch: 'Ho Chi Minh City Branch',
      qrCodeUrl: '',
      transferInstructions:
        'Chuyển khoản chính xác số tiền và ghi đúng mã giao dịch vào nội dung chuyển tiền.',
    },
  });

  const apiOrigin = domains.apiOrigin || 'https://api.sophiaplatform.com';
  const stripeWebhookUrl = `${apiOrigin}/api/v1/webhooks/stripe`;
  const paypalWebhookUrl = `${apiOrigin}/api/v1/webhooks/paypal`;
  const paddleWebhookUrl = `${apiOrigin}/api/v1/webhooks/paddle`;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await adminClient.get<{ success: boolean; data: SystemSettings }>(
        '/api/v1/admin/settings'
      );
      if (res && res.data?.paymentGateways) {
        setDraft(res.data.paymentGateways);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const payload: UpdateSystemSettingsInput = {
        paymentGateways: draft,
      };

      await adminClient.put('/api/v1/admin/settings', payload);
      setSuccessMsg(t('admin.gateways.saveSuccess'));
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMsg(msg || t('admin.gateways.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <span className="text-xs text-foreground-muted">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-surface-border">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2.5">
            <CreditCard className="w-6 h-6 text-primary" />
            {t('admin.gateways.title')}
          </h1>
          <p className="text-xs text-foreground-muted mt-1">
            {t('admin.gateways.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={fetchSettings}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-medium transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>{t('common.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-fg hover:bg-primary-hover active:scale-[0.98] text-xs font-semibold shadow-md transition-all disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('admin.gateways.saving')}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>{t('admin.gateways.saveChanges')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status Alerts */}
      {successMsg && (
        <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 text-xs flex items-center gap-3 animate-fade-in font-medium">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-500 text-xs flex items-center gap-3 animate-fade-in font-medium">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ========================================================= */}
        {/* 1. Stripe Payments Card */}
        {/* ========================================================= */}
        <div className="rounded-2xl border border-surface-border bg-surface p-6 shadow-sm space-y-5">
          <div className="flex items-start justify-between border-b border-surface-border pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shadow-sm">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground">
                  {t('admin.gateways.stripe.title')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  {t('admin.gateways.stripe.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Sandbox toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    stripe: { ...prev.stripe, sandbox: !prev.stripe.sandbox },
                  }))
                }
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                  draft.stripe.sandbox
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-500'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                }`}
              >
                {draft.stripe.sandbox
                  ? t('admin.gateways.sandboxMode')
                  : t('admin.gateways.liveMode')}
              </button>

              {/* Enabled toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    stripe: { ...prev.stripe, enabled: !prev.stripe.enabled },
                  }))
                }
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  draft.stripe.enabled ? 'bg-primary' : 'bg-surface-border'
                }`}
                aria-label="Toggle Stripe"
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    draft.stripe.enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="space-y-4 text-xs">
            {/* Publishable Key */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.stripe.publishableKey')}
              </label>
              <input
                type="text"
                value={draft.stripe.publishableKey}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    stripe: { ...prev.stripe, publishableKey: e.target.value },
                  }))
                }
                placeholder={t('admin.gateways.stripe.publishableKeyPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Secret Key */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.stripe.secretKey')}
              </label>
              <div className="relative">
                <input
                  type={showStripeSecret ? 'text' : 'password'}
                  value={draft.stripe.secretKey}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      stripe: { ...prev.stripe, secretKey: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.stripe.secretKeyPlaceholder')}
                  className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowStripeSecret(!showStripeSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                >
                  {showStripeSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Webhook Secret */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.stripe.webhookSecret')}
              </label>
              <div className="relative">
                <input
                  type={showStripeWebhook ? 'text' : 'password'}
                  value={draft.stripe.webhookSecret}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      stripe: { ...prev.stripe, webhookSecret: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.stripe.webhookSecretPlaceholder')}
                  className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowStripeWebhook(!showStripeWebhook)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                >
                  {showStripeWebhook ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Webhook Ingestion URL Box */}
            <div className="p-3 rounded-xl border border-surface-border bg-muted/40 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-foreground-muted">
                <span>{t('admin.gateways.webhookUrl')}</span>
                <button
                  type="button"
                  onClick={() => handleCopy(stripeWebhookUrl, 'stripe')}
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {copiedKey === 'stripe' ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="text-emerald-500">{t('admin.gateways.copied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>{t('admin.gateways.copyWebhook')}</span>
                    </>
                  )}
                </button>
              </div>
              <code className="block text-[11px] font-mono text-foreground break-all select-all">
                {stripeWebhookUrl}
              </code>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 2. PayPal v2 Card */}
        {/* ========================================================= */}
        <div className="rounded-2xl border border-surface-border bg-surface p-6 shadow-sm space-y-5">
          <div className="flex items-start justify-between border-b border-surface-border pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-500 flex items-center justify-center shadow-sm">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground">
                  {t('admin.gateways.paypal.title')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  {t('admin.gateways.paypal.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Sandbox toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    paypal: { ...prev.paypal, sandbox: !prev.paypal.sandbox },
                  }))
                }
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                  draft.paypal.sandbox
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-500'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                }`}
              >
                {draft.paypal.sandbox
                  ? t('admin.gateways.sandboxMode')
                  : t('admin.gateways.liveMode')}
              </button>

              {/* Enabled toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    paypal: { ...prev.paypal, enabled: !prev.paypal.enabled },
                  }))
                }
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  draft.paypal.enabled ? 'bg-primary' : 'bg-surface-border'
                }`}
                aria-label="Toggle PayPal"
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    draft.paypal.enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="space-y-4 text-xs">
            {/* Client ID */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.paypal.clientId')}
              </label>
              <input
                type="text"
                value={draft.paypal.clientId}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    paypal: { ...prev.paypal, clientId: e.target.value },
                  }))
                }
                placeholder={t('admin.gateways.paypal.clientIdPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Client Secret */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.paypal.clientSecret')}
              </label>
              <div className="relative">
                <input
                  type={showPaypalSecret ? 'text' : 'password'}
                  value={draft.paypal.clientSecret}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      paypal: { ...prev.paypal, clientSecret: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.paypal.clientSecretPlaceholder')}
                  className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPaypalSecret(!showPaypalSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                >
                  {showPaypalSecret ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Webhook ID */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.paypal.webhookId')}
              </label>
              <input
                type="text"
                value={draft.paypal.webhookId}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    paypal: { ...prev.paypal, webhookId: e.target.value },
                  }))
                }
                placeholder={t('admin.gateways.paypal.webhookIdPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Webhook Ingestion URL Box */}
            <div className="p-3 rounded-xl border border-surface-border bg-muted/40 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-foreground-muted">
                <span>{t('admin.gateways.webhookUrl')}</span>
                <button
                  type="button"
                  onClick={() => handleCopy(paypalWebhookUrl, 'paypal')}
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {copiedKey === 'paypal' ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="text-emerald-500">{t('admin.gateways.copied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>{t('admin.gateways.copyWebhook')}</span>
                    </>
                  )}
                </button>
              </div>
              <code className="block text-[11px] font-mono text-foreground break-all select-all">
                {paypalWebhookUrl}
              </code>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 3. Paddle Billing (MoR) Card */}
        {/* ========================================================= */}
        <div className="rounded-2xl border border-surface-border bg-surface p-6 shadow-sm space-y-5">
          <div className="flex items-start justify-between border-b border-surface-border pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center shadow-sm">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground">
                  {t('admin.gateways.paddle.title')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  {t('admin.gateways.paddle.description')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Sandbox toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    paddle: { ...prev.paddle, sandbox: !prev.paddle.sandbox },
                  }))
                }
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${
                  draft.paddle.sandbox
                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-500'
                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                }`}
              >
                {draft.paddle.sandbox
                  ? t('admin.gateways.sandboxMode')
                  : t('admin.gateways.liveMode')}
              </button>

              {/* Enabled toggle */}
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => ({
                    ...prev,
                    paddle: { ...prev.paddle, enabled: !prev.paddle.enabled },
                  }))
                }
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  draft.paddle.enabled ? 'bg-primary' : 'bg-surface-border'
                }`}
                aria-label="Toggle Paddle"
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    draft.paddle.enabled ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="space-y-4 text-xs">
            {/* Vendor ID & Client Token in 2 cols */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="font-medium text-foreground">
                  {t('admin.gateways.paddle.vendorId')}
                </label>
                <input
                  type="text"
                  value={draft.paddle.vendorId}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      paddle: { ...prev.paddle, vendorId: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.paddle.vendorIdPlaceholder')}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-medium text-foreground">
                  {t('admin.gateways.paddle.clientSideToken')}
                </label>
                <input
                  type="text"
                  value={draft.paddle.clientSideToken}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      paddle: { ...prev.paddle, clientSideToken: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.paddle.clientSideTokenPlaceholder')}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
              </div>
            </div>

            {/* API Secret Key */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.paddle.apiKey')}
              </label>
              <div className="relative">
                <input
                  type={showPaddleApiKey ? 'text' : 'password'}
                  value={draft.paddle.apiKey}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      paddle: { ...prev.paddle, apiKey: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.paddle.apiKeyPlaceholder')}
                  className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPaddleApiKey(!showPaddleApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                >
                  {showPaddleApiKey ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Webhook Secret Key */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.paddle.webhookSecret')}
              </label>
              <div className="relative">
                <input
                  type={showPaddleWebhook ? 'text' : 'password'}
                  value={draft.paddle.webhookSecret}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      paddle: { ...prev.paddle, webhookSecret: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.paddle.webhookSecretPlaceholder')}
                  className="w-full pl-3.5 pr-10 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPaddleWebhook(!showPaddleWebhook)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted hover:text-foreground"
                >
                  {showPaddleWebhook ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Webhook Ingestion URL Box */}
            <div className="p-3 rounded-xl border border-surface-border bg-muted/40 space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold text-foreground-muted">
                <span>{t('admin.gateways.webhookUrl')}</span>
                <button
                  type="button"
                  onClick={() => handleCopy(paddleWebhookUrl, 'paddle')}
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  {copiedKey === 'paddle' ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="text-emerald-500">{t('admin.gateways.copied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>{t('admin.gateways.copyWebhook')}</span>
                    </>
                  )}
                </button>
              </div>
              <code className="block text-[11px] font-mono text-foreground break-all select-all">
                {paddleWebhookUrl}
              </code>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* 4. Bank Transfer (VietQR) Card */}
        {/* ========================================================= */}
        <div className="rounded-2xl border border-surface-border bg-surface p-6 shadow-sm space-y-5">
          <div className="flex items-start justify-between border-b border-surface-border pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center shadow-sm">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-sm text-foreground">
                  {t('admin.gateways.bankTransfer.title')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  {t('admin.gateways.bankTransfer.description')}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                setDraft((prev) => ({
                  ...prev,
                  bankTransfer: {
                    ...prev.bankTransfer,
                    enabled: !prev.bankTransfer.enabled,
                  },
                }))
              }
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                draft.bankTransfer.enabled ? 'bg-primary' : 'bg-surface-border'
              }`}
              aria-label="Toggle Bank Transfer"
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                  draft.bankTransfer.enabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <div className="space-y-4 text-xs">
            {/* Bank Name */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.bankTransfer.bankName')}
              </label>
              <input
                type="text"
                value={draft.bankTransfer.bankName}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    bankTransfer: { ...prev.bankTransfer, bankName: e.target.value },
                  }))
                }
                placeholder={t('admin.gateways.bankTransfer.bankNamePlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Account Number & Holder in 2 cols */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="font-medium text-foreground">
                  {t('admin.gateways.bankTransfer.accountNumber')}
                </label>
                <input
                  type="text"
                  value={draft.bankTransfer.accountNumber}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      bankTransfer: { ...prev.bankTransfer, accountNumber: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.bankTransfer.accountNumberPlaceholder')}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              <div className="space-y-1.5">
                <label className="font-medium text-foreground">
                  {t('admin.gateways.bankTransfer.accountHolder')}
                </label>
                <input
                  type="text"
                  value={draft.bankTransfer.accountHolder}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      bankTransfer: { ...prev.bankTransfer, accountHolder: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.bankTransfer.accountHolderPlaceholder')}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-semibold text-xs focus:outline-none focus:border-primary transition-colors uppercase"
                />
              </div>
            </div>

            {/* Branch */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.bankTransfer.branch')}
              </label>
              <input
                type="text"
                value={draft.bankTransfer.branch}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    bankTransfer: { ...prev.bankTransfer, branch: e.target.value },
                  }))
                }
                placeholder={t('admin.gateways.bankTransfer.branchPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs focus:outline-none focus:border-primary transition-colors"
              />
            </div>

            {/* Custom QR Code Image URL */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground flex items-center justify-between">
                <span>{t('admin.gateways.bankTransfer.qrCodeUrl')}</span>
                <span className="text-[10px] text-foreground-muted">Auto VietQR fallback</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={draft.bankTransfer.qrCodeUrl}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      bankTransfer: { ...prev.bankTransfer, qrCodeUrl: e.target.value },
                    }))
                  }
                  placeholder={t('admin.gateways.bankTransfer.qrCodeUrlPlaceholder')}
                  className="flex-1 px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground font-mono text-xs focus:outline-none focus:border-primary transition-colors"
                />
              </div>
            </div>

            {/* Transfer Instructions */}
            <div className="space-y-1.5">
              <label className="font-medium text-foreground">
                {t('admin.gateways.bankTransfer.instructions')}
              </label>
              <textarea
                rows={2}
                value={draft.bankTransfer.transferInstructions}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    bankTransfer: {
                      ...prev.bankTransfer,
                      transferInstructions: e.target.value,
                    },
                  }))
                }
                placeholder={t('admin.gateways.bankTransfer.instructionsPlaceholder')}
                className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs focus:outline-none focus:border-primary transition-colors resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
